import React, { InputHTMLAttributes } from 'react';

import { Controller, useFormContext } from 'react-hook-form';

import { Theme, makeStyles } from '@material-ui/core';
import { BrandColorsObject } from 'app-types/returns';
import { twMerge } from 'tailwind-merge';
import { getColorContrast } from 'ui';
import { TextInput } from 'ui/atoms';

import { getCalculatedBrightnessFromHex } from '../../../../old-ui';

export interface IPortalTextInputRhf extends InputHTMLAttributes<HTMLInputElement> {
  error?: boolean;
  useSecondaryBg?: boolean;
  inputClassName?: string;
  centeredPlaceholderText?: boolean;
  colors?: BrandColorsObject;
}

type StyleProps = {
  inputBgL1: string;
  inputBgL2: string;
  inputBgL3: string;
  inputOutlineColor: string;
  inputTextColor: string;
  centeredPlaceholderText: boolean;
};

const useStyles = makeStyles<
  Theme,
  {
    inputBgL1: string;
    inputBgL2: string;
    inputBgL3: string;
    inputOutlineColor: string;
    inputTextColor: string;
    centeredPlaceholderText: boolean;
  }
>({
  inputBackground: {
    backgroundColor: (props: StyleProps) => `${props.inputBgL1} !important`,
    color: (props: StyleProps) => `${props.inputTextColor} !important`,
    '&:hover': {
      backgroundColor: (props: StyleProps) => `${props.inputBgL2} !important`,
      borderWidth: '0px',
    },
    '&:focus': {
      backgroundColor: (props: StyleProps) => `${props.inputBgL1} !important`,
      outlineWidth: '1px',
      outlineOffset: '0px',
      borderWidth: '0px',
      outlineColor: (props: StyleProps) => `${props.inputOutlineColor} !important`,
    },
    '&::placeholder': {
      color: (props: StyleProps) => `${props.inputBgL3} !important`,
      textAlign: (props: StyleProps) => (props.centeredPlaceholderText ? 'center' : undefined),
    },
    '&:disabled': {
      color: (props: StyleProps) => `${props.inputBgL3} !important`,
    },
  },
});

const PortalTextInputRhf = ({
  error = false,
  className = '',
  inputClassName = '',
  centeredPlaceholderText = false,
  colors,
  ...rest
}: IPortalTextInputRhf) => {
  const { control } = useFormContext();

  const textColor = getColorContrast('#FFFFFF', true);
  const inputBgL1 = getCalculatedBrightnessFromHex('#FFFFFF', 1);
  const inputBgL2 = getCalculatedBrightnessFromHex('#FFFFFF', 2);
  const inputOutlineColor = getCalculatedBrightnessFromHex(colors?.brand || '#FFFFFF', 2);
  const inputBgL3 = getCalculatedBrightnessFromHex('#FFFFFF', 3);

  const classes = useStyles({
    inputBgL1,
    inputBgL2,
    inputBgL3,
    inputOutlineColor,
    inputTextColor: colors?.black || '#000000',
    centeredPlaceholderText,
  });

  return (
    <Controller
      name={rest?.name || ''}
      defaultValue={''}
      control={control}
      shouldUnregister
      render={({ field: { onChange, value } }) => {
        return (
          <TextInput
            isError={error}
            className={twMerge(className)}
            inputClassName={twMerge(
              'transition-all',

              classes.inputBackground,
              textColor,
              inputClassName,
            )}
            backgroundStyle={{ borderWidth: '0px' }}
            id={rest?.name}
            name={rest?.name || ''}
            onChange={onChange}
            value={value}
            {...rest}
          />
        );
      }}
    />
  );
};

export { PortalTextInputRhf };
